import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, Observable, Subject} from 'rxjs';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {
    AbstractControl,
    ControlValueAccessor,
    UntypedFormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import {DataService} from '../../services/data.service';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {AsyncPipe} from '@angular/common';

@Component({
    selector: 'app-country-selector',
    template: `
        <mat-form-field [appearance]="appearance">
            <mat-label>Country</mat-label>
            <input matInput [matAutocomplete]="autocomplete" [required]="required" [formControl]="control">
            <mat-autocomplete #autocomplete=matAutocomplete [displayWith]="countryDisplayFn">
                @for (country of (filteredCountries$ | async) ?? []; track country) {
                    <mat-option [value]="country">
                        {{ country }}
                    </mat-option>
                }
            </mat-autocomplete>

            @if (!(countries$ | async)) {
                <ng-container matSuffix>
                    <mat-progress-spinner mode="indeterminate" strokeWidth="2" diameter="20"></mat-progress-spinner>
                </ng-container>
            }
        </mat-form-field>
    `,
    imports: [
        MatAutocomplete,
        MatLabel,
        MatOption,
        MatFormField,
        MatProgressSpinner,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatInput,
        AsyncPipe
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountrySelectorComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CountrySelectorComponent),
            multi: true
        }
    ]
})
export class CountrySelectorComponent implements OnDestroy, OnInit, ControlValueAccessor, Validator {
    countries$: Observable<string[]>;
    filteredCountries$: Observable<string[]>;
    @Input() required = false;
    @Input() appearance: 'fill' | 'outline' = 'fill';
    control = new UntypedFormControl(null);

    private unsubscribe$ = new Subject<void>();

    private onChange: (val: any) => void = () => {
    };
    private onTouched = () => {
    };

    constructor(
        private dataService: DataService
    ) {
        this.countries$ = this.dataService.getCountries();

        this.filteredCountries$ = combineLatest([
            this.countries$,
            this.control.valueChanges.pipe(startWith(''))
        ]).pipe(
            takeUntil(this.unsubscribe$),
            map(([countries, value]) => {
                const filter = (typeof value === 'string' ? value : '').toLowerCase();
                return countries.filter(c => c.toLowerCase().includes(filter));
            })
        );

        this.control.valueChanges.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((value) => {
            this.onChange(value);
            this.onTouched();
        });
    }

    ngOnInit(): void {
        if (this.required) {
            this.control.setValidators(Validators.required);
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    countryDisplayFn(value: any) {
        return value || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.control.disable() : this.control.enable();
    }

    writeValue(value: string): void {
        this.control.setValue(value);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.required && !control.value) {
            return {required: true};
        }
        if (control.value && typeof control.value !== 'string') {
            return {invalidCountry: true};
        }
        return null;
    }
}
